import { useTranslation } from "react-i18next";
import { discoverFormVariables } from "../template-management-form.js";
import type { TemplateFormState } from "../template-management-form.js";

export interface TemplateVariableExamplesProps {
  state: TemplateFormState;
  onChange: (state: TemplateFormState) => void;
  disabled?: boolean;
}

const labelClass = "grid gap-1 text-[0.85rem] text-muted";
const inputClass = "w-full rounded-sm border border-border bg-surface px-3 py-2 text-foreground";

export function TemplateVariableExamples({
  state,
  onChange,
  disabled = false,
}: TemplateVariableExamplesProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const { headerKeys, bodyKeys, urlButtonKeys } = discoverFormVariables(state);

  const hasExamples =
    headerKeys.length > 0 || bodyKeys.length > 0 || urlButtonKeys.some((entry) => entry.keys.length > 0);

  if (!hasExamples) return null;

  return (
    <section
      className="grid gap-2 rounded-md border border-dashed border-border bg-surface-muted p-3"
      aria-labelledby="template-examples-heading"
    >
      <h3 id="template-examples-heading" className="m-0 text-[0.95rem] font-semibold">
        {t("whatsapp.templates.manage.examples.title")}
      </h3>
      <p className="m-0 text-[0.85rem] leading-[1.45] text-muted">
        {t("whatsapp.templates.manage.examples.hint")}
      </p>

      {headerKeys.length === 1 ? (
        <label className={labelClass}>
          {t("whatsapp.templates.manage.examples.header", { key: headerKeys[0] })}
          <input
            className={inputClass}
            value={state.headerExample}
            disabled={disabled}
            onChange={(event) => onChange({ ...state, headerExample: event.target.value })}
          />
        </label>
      ) : null}

      {bodyKeys.map((key) => (
        <label key={`body-example-${key}`} className={labelClass}>
          {t("whatsapp.templates.manage.examples.body", { key: `{{${key}}}` })}
          <input
            className={inputClass}
            value={state.bodyExamples[key] ?? ""}
            disabled={disabled}
            onChange={(event) =>
              onChange({
                ...state,
                bodyExamples: { ...state.bodyExamples, [key]: event.target.value },
              })
            }
          />
        </label>
      ))}

      {urlButtonKeys.map((entry) =>
        entry.keys.length === 1 ? (
          <label key={`url-example-${entry.buttonIndex}`} className={labelClass}>
            {t("whatsapp.templates.manage.examples.urlButton", {
              index: entry.buttonIndex + 1,
              key: `{{${entry.keys[0]}}}`,
            })}
            <input
              className={inputClass}
              value={state.urlButtonExamples[entry.buttonIndex] ?? ""}
              disabled={disabled}
              onChange={(event) =>
                onChange({
                  ...state,
                  urlButtonExamples: {
                    ...state.urlButtonExamples,
                    [entry.buttonIndex]: event.target.value,
                  },
                })
              }
            />
          </label>
        ) : null,
      )}
    </section>
  );
}
